import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listContactSubmissions, type ContactSubmission } from "@/integrations/backend/contact-submissions";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type ContactRow = ContactSubmission;

const contactSubmissionsKey = queryKeys.admin.contactSubmissions;

export default function ContactSubmissionsView() {
  const submissionsQuery = useQuery({
    queryKey: contactSubmissionsKey,
    queryFn: listContactSubmissions,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...contactSubmissionsKey]),
  });
  const rows: ContactRow[] = submissionsQuery.data ?? [];
  const loading = submissionsQuery.isLoading;

  const [selected, setSelected] = useState<ContactRow | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Contact Submissions</h1>
        <p className="text-gray-600">All messages sent from the public contact form.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inbox</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading &&
                  rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => {
                        setSelected(r);
                        setIsDialogOpen(true);
                      }}
                    >
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.phone || "-"}</TableCell>
                      <TableCell>{r.subject || "-"}</TableCell>
                      <TableCell>
                        {r.created_at ? new Date(r.created_at).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(r);
                          setIsDialogOpen(true);
                        }}
                      >
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-gray-500">
                      No submissions yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setSelected(null);
          }
        }}
      >
        <DialogContent className="max-w-lg bg-white text-black">
          <DialogHeader>
            <DialogTitle>Contact submission details</DialogTitle>
            <DialogDescription>
              View the full message and contact information for this submission.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">Name</div>
                <div className="text-sm">{selected.name}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Email</div>
                  <div className="text-sm break-all">{selected.email}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Phone</div>
                  <div className="text-sm">{selected.phone || "-"}</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">Subject</div>
                <div className="text-sm">{selected.subject || "-"}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">Submitted</div>
                <div className="text-sm">
                  {selected.created_at
                    ? new Date(selected.created_at).toLocaleString()
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">Message</div>
                <div className="mt-1 rounded-md border bg-gray-50 px-3 py-2 text-sm whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="text-black border-gray-300 hover:bg-gray-100"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}




